import { initializeApp, getApps } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore, collection, addDoc, serverTimestamp } from "firebase/firestore";
import { getStorage } from "firebase/storage";

function readConfig(prefixes) {
  const config = {
    apiKey: undefined,
    authDomain: undefined,
    projectId: undefined,
    storageBucket: undefined,
    messagingSenderId: undefined,
    appId: undefined,
  };

  for (const prefix of prefixes) {
    config.apiKey ??= process.env[`${prefix}API_KEY`];
    config.authDomain ??= process.env[`${prefix}AUTH_DOMAIN`];
    config.projectId ??= process.env[`${prefix}PROJECT_ID`];
    config.storageBucket ??= process.env[`${prefix}STORAGE_BUCKET`];
    config.messagingSenderId ??= process.env[`${prefix}MESSAGING_ID`];
    config.appId ??= process.env[`${prefix}APP_ID`];
  }

  return config;
}

function hasRequiredConfig(config) {
  return Boolean(
    config.apiKey &&
    config.authDomain &&
    config.projectId &&
    config.appId
  );
}

const externalConfig = readConfig([
  'REACT_APP_DB_',
  'REACT_APP_FIREBASE_',
]);

let dbExternal = null;
let authExternal = null;
let storageExternal = null;
let appExternal = null;
let externalInitError = null;

function getExternalApp() {
  if (appExternal || externalInitError) {
    return appExternal;
  }

  if (!hasRequiredConfig(externalConfig)) {
    externalInitError = new Error(
      'Firebase database configuration is missing. Set REACT_APP_DB_* or REACT_APP_FIREBASE_* environment variables before using forms.'
    );
    return null;
  }

  try {
    const appName = 'MAGELLAN_EXTERNAL_DB';
    const existingApp = getApps().find((app) => app.name === appName);
    appExternal = existingApp || initializeApp(externalConfig, appName);
    return appExternal;
  } catch (error) {
    externalInitError = error;
    console.error('Error initializing Firebase for website forms:', error);
    return null;
  }
}

function getExternalDb() {
  if (dbExternal || externalInitError) {
    return dbExternal;
  }

  const app = getExternalApp();
  if (!app) {
    return null;
  }

  dbExternal = getFirestore(app);
  return dbExternal;
}

function getExternalAuth() {
  if (authExternal || externalInitError) {
    return authExternal;
  }

  const app = getExternalApp();
  if (!app) {
    return null;
  }

  authExternal = getAuth(app);
  return authExternal;
}

function getExternalStorage() {
  if (storageExternal || externalInitError) {
    return storageExternal;
  }

  const app = getExternalApp();
  if (!app) {
    return null;
  }

  storageExternal = getStorage(app);
  return storageExternal;
}

export async function submitToCRM(collectionName, formData) {
  try {
    const db = getExternalDb();

    if (!db) {
      throw externalInitError || new Error('Firebase database is not configured.');
    }

    const inquiriesRef = collection(db, collectionName);
    const docRef = await addDoc(inquiriesRef, {
      ...formData,
      source: "magellan.international",
      submittedAt: serverTimestamp(),
    });

    return { success: true, id: docRef.id };
  } catch (error) {
    console.error(`Error writing to database [${collectionName}]:`, error);
    return { success: false, error: error.message };
  }
}

export { getExternalDb, getExternalAuth, getExternalStorage };
